<script setup lang="ts">
const { locales: i18nLocales } = useI18n();
const locales = i18nLocales.value.map((l) =>
  typeof l === "string" ? l : l.code
);
const { partnerUrl } = useRuntimeConfig().public;
const router = useRouter();
const localePath = useLocalePath();
const blockedRoutes = ["/mobile-verification", "/complete"];

const cleanPath = (url: string) => {
  if (!url) return "";
  let path = url;
  try {
    if (path.startsWith("http")) {
      path = new URL(path).pathname;
    }
  } catch {
    // ignore
  }

  const pathWithoutQuery = path.split("?")[0] || "";
  let finalPath = pathWithoutQuery;

  for (const l of locales) {
    if (finalPath.startsWith(`/${l}/`)) {
      finalPath = finalPath.substring(l.length + 1);
      if (!finalPath.startsWith("/")) finalPath = "/" + finalPath;
      break;
    } else if (finalPath === `/${l}`) {
      finalPath = "/";
      break;
    }
  }
  return finalPath;
};

// --- Detection Logic ---

const getBackRoute = () => {
  if (import.meta.server) {
    const referer = useRequestHeader("referer");
    const host = useRequestHeader("host");
    if (referer && host && referer.includes(host)) {
      return referer;
    }
    return "";
  } else {
    const state = window.history.state;
    const referrer = document.referrer;
    const currentHost = window.location.host;

    if (state && state.back) {
      return state.back;
    } else if (referrer && referrer.includes(currentHost)) {
      return referrer;
    }
    return "";
  }
};

const checkVisibility = (route: string) => {
  if (!route) return false;
  const cleanedPath = cleanPath(route);
  return !blockedRoutes.includes(cleanedPath);
};

// Initialize ref based on available data (Server or Client)
const showBackButton = ref(checkVisibility(getBackRoute()));

const updateBackButton = () => {
  showBackButton.value = checkVisibility(getBackRoute());
};

// Final client-side checks/updates
if (import.meta.client) {
  // Update again on client in case history state is different from referer
  updateBackButton();

  router.afterEach(() => {
    updateBackButton();
  });
}

onMounted(() => {
  updateBackButton();
});

const goBack = () => {
  router.back();
};
</script>

<template>
  <div
    class="shrink-0 w-full fixed lg:relative top-0 lg:top-auto z-50 bg-white border-b border-gray-100"
  >
    <div class="flex items-center justify-between px-4 xs:px-5 py-4 xs:py-5">
      <div class="flex items-center gap-x-2">
        <div v-if="showBackButton" class="shrink-0">
          <UButton
            icon="i-approved-arrow-left"
            size="xl"
            class="rounded-xl"
            color="gray"
            variant="ghost"
            :ui="{ square: { xl: 'p-2' } }"
            @click="goBack"
          />
        </div>
        <ULink :to="localePath('/')" class="shrink-0 outline-none">
          <img src="/h-logo.svg" alt="bookforme" class="w-43 xs:w-auto" >
        </ULink>
      </div>

      <div class="flex items-center gap-x-2">
        <UButton
          :to="partnerUrl"
          target="_blank"
          :label="$t('page.sign_in.for_partners')"
          size="xl"
          :trailing="true"
          color="dodger-blue"
          class="hidden lg:inline-flex"
          :ui="{
            rounded: 'rounded-full',
            size: { xl: 'lg:text-base' },
            gap: { xl: 'gap-x-1' },
            padding: { xl: 'px-3 xs:px-3.5 py-2 xs:py-2.5' },
          }"
        />
        <CommonLanguagesMenu size="size-8 lg:size-10" />
      </div>
    </div>
  </div>
</template>
